import { Alert, Button, Empty, Grid, Select, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, defaultYear, useAcademicYears, type ClassListItem } from "../app/classes.ts";
import { ClassDrawer } from "../components/ClassDrawer.tsx";

export function ClassesPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("academic.manage");
  const years = useAcademicYears();
  const [chosenYearId, setChosenYearId] = useState<string>();
  const [creating, setCreating] = useState(false);
  const year = years.data?.find((y) => y.id === chosenYearId) ?? defaultYear(years.data);

  const classes = useQuery({
    queryKey: classKeys.list(year?.id),
    enabled: year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const columns = [
    { title: t("classes.name"), dataIndex: "name", key: "name", width: 100, render: (name: string, c: ClassListItem) => <Link to={`/academic/classes/${c.id}`}>{name}</Link> },
    { title: t("classes.grade"), dataIndex: "grade", key: "grade", width: 80, responsive: ["sm" as const] },
    { title: t("classes.course"), dataIndex: "courseName", key: "courseName", ellipsis: true },
    {
      title: t("classes.teachers"),
      key: "teachers",
      width: 130,
      render: (_: unknown, c: ClassListItem) => {
        const done = Number(c.subjectsWithTeacher);
        const total = Number(c.subjectCount);
        return <Tag color={done >= total && total > 0 ? "green" : "orange"}>{t("classes.teachersAssigned", { done, total })}</Tag>;
      },
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("classes.title")}
        </Typography.Title>
        <Space wrap>
          <Select
            aria-label={t("classes.year")}
            style={{ width: screens.md ? 200 : 160 }}
            loading={years.isPending}
            value={year?.id}
            onChange={setChosenYearId}
            options={(years.data ?? []).map((y) => ({ value: y.id, label: y.label }))}
          />
          {canManage && (
            <Button type="primary" icon={<PlusOutlined />} disabled={!year || year.status === "Closed"} onClick={() => setCreating(true)}>
              {t("classes.create")}
            </Button>
          )}
        </Space>
      </div>
      {(years.isError || classes.isError) && <Alert type="error" showIcon title={t("app.error")} />}
      {years.isSuccess && years.data.length === 0 ? (
        <Empty description={t("classes.noYear")} />
      ) : (
        <Table<ClassListItem>
          rowKey="id"
          size={screens.md ? "middle" : "small"}
          loading={classes.isPending && year !== undefined}
          columns={columns}
          dataSource={classes.data ?? []}
          locale={{ emptyText: t("classes.empty") }}
          pagination={false}
          onRow={(c) => ({ onClick: () => navigate(`/academic/classes/${c.id}`), style: { cursor: "pointer" } })}
        />
      )}
      <ClassDrawer
        open={creating}
        year={year}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          void message.success(t("classes.created", { name: created.name }));
          navigate(`/academic/classes/${created.id}`);
        }}
      />
    </Space>
  );
}
